import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { formatDate , getApiErrorMessage } from "../lib/utils";
import { safeFilter, safeIncludes } from "../lib/safeUtils";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Building2,
  Plus,
  MoreVertical,
  Mail,
  Phone,
  MapPin,
  Trash2,
  Edit,
  Package,
  Search,
  X,
} from "lucide-react";
import { Badge } from "../components/ui/badge";

const SUPPLIER_CATEGORIES = [
  "Materiales",
  "Equipos",
  "Servicios",
  "Herramientas",
  "Consumibles",
  "Logística",
  "Otro",
];

export const Suppliers = () => {
  const { api, company } = useAuth();
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [searchFilter, setSearchFilter] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    rfc: "",
    category: "",
    notes: "",
  });

  useEffect(() => {
    if (company?.id) {
      fetchSuppliers();
    }
  }, [company]);

  const fetchSuppliers = async () => {
    try {
      const response = await api.get(`/suppliers?company_id=${company.id}`);
      setSuppliers(response.data);
    } catch (error) {
      toast.error("Error al cargar proveedores");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSupplier) {
        // For edit, we'd need a PUT endpoint - for now just create new
        toast.info("Funcionalidad de edición en desarrollo");
      } else {
        await api.post("/suppliers", {
          company_id: company.id,
          ...formData,
        });
        toast.success("Proveedor registrado exitosamente");
      }
      setDialogOpen(false);
      resetForm();
      fetchSuppliers();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error al guardar proveedor"));
    }
  };

  const handleDelete = async (supplierId) => {
    if (!window.confirm("¿Estás seguro de eliminar este proveedor?")) return;
    try {
      await api.delete(`/suppliers/${supplierId}`);
      toast.success("Proveedor eliminado");
      fetchSuppliers();
    } catch (error) {
      toast.error("Error al eliminar proveedor");
    }
  };

  const resetForm = () => {
    setEditingSupplier(null);
    setFormData({
      name: "",
      contact_name: "",
      email: "",
      phone: "",
      address: "",
      rfc: "",
      category: "",
      notes: "",
    });
  };

  const openNewSupplierDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const getCategoryCount = (category) => {
    return safeFilter(suppliers, (s) => s && s.category === category).length;
  };

  // Filter suppliers based on search
  const filteredSuppliers = safeFilter(suppliers, (supplier) => {
    if (!supplier) return false;
    if (!searchFilter) return true;
    const search = searchFilter.toLowerCase();
    return (
      safeIncludes(supplier.name, search) ||
      safeIncludes(supplier.contact_name, search) ||
      safeIncludes(supplier.email, search) ||
      safeIncludes(supplier.phone, search) ||
      safeIncludes(supplier.rfc, search) ||
      safeIncludes(supplier.category, search) ||
      safeIncludes(supplier.address, search)
    );
  });

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="suppliers-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-[Chivo] text-slate-900">Proveedores</h1>
          <p className="text-muted-foreground">Base de datos de proveedores</p>
        </div>
        <Button className="btn-industrial" onClick={openNewSupplierDialog} data-testid="add-supplier-btn">
          <Plus className="mr-2 h-4 w-4" />
          Nuevo Proveedor
        </Button>
      </div>

      {/* Category Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {SUPPLIER_CATEGORIES.map((cat) => (
          <Card key={cat} className="p-3">
            <div className="text-2xl font-bold font-[Chivo]">{getCategoryCount(cat)}</div>
            <div className="text-xs text-muted-foreground">{cat}</div>
          </Card>
        ))}
      </div>

      {/* Suppliers Table */}
      <Card data-testid="suppliers-table-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-primary" />
            Directorio de Proveedores
          </CardTitle>
          <CardDescription>
            {suppliers.length} proveedor(es) registrado(s)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Search Filter */}
          <div className="flex items-center gap-2 mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre, contacto, email, categoría..."
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                className="pl-9"
                data-testid="suppliers-search-filter"
              />
              {searchFilter && (
                <button
                  onClick={() => setSearchFilter("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {searchFilter && (
              <Badge variant="secondary" className="flex items-center gap-1">
                Filtro activo: "{searchFilter}"
              </Badge>
            )}
          </div>
          <div className="rounded-sm border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Proveedor</TableHead>
                  <TableHead>Contacto</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Teléfono</TableHead>
                  <TableHead>Categoría</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSuppliers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      {searchFilter ? "No se encontraron proveedores" : "No hay proveedores registrados"}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSuppliers.map((supplier) => (
                    <TableRow key={supplier.id} data-testid={`supplier-row-${supplier.id}`}>
                      <TableCell>
                        <div className="font-medium">{supplier.name}</div>
                        {supplier.rfc && (
                          <div className="text-sm text-muted-foreground font-mono">{supplier.rfc}</div>
                        )}
                      </TableCell>
                      <TableCell>{supplier.contact_name || "-"}</TableCell>
                      <TableCell>
                        {supplier.email ? (
                          <a href={`mailto:${supplier.email}`} className="text-primary hover:underline">
                            {supplier.email}
                          </a>
                        ) : (
                          "-"
                        )}
                      </TableCell>
                      <TableCell>{supplier.phone || "-"}</TableCell>
                      <TableCell>
                        <span className="inline-flex items-center px-2 py-1 rounded-full bg-slate-100 text-xs">
                          {supplier.category || "Sin categoría"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onClick={() => handleDelete(supplier.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Eliminar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create Supplier Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>Nuevo Proveedor</DialogTitle>
              <DialogDescription>
                Registra un nuevo proveedor en la base de datos
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="name">Nombre del Proveedor *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Proveedor S.A. de C.V."
                  required
                  data-testid="supplier-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="contact_name">Contacto</Label>
                  <Input
                    id="contact_name"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    placeholder="Juan Pérez"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="rfc">RFC</Label>
                  <Input
                    id="rfc"
                    value={formData.rfc}
                    onChange={(e) => setFormData({ ...formData, rfc: e.target.value.toUpperCase() })}
                    placeholder="ABC123456XYZ"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="contacto@proveedor.com"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="phone">Teléfono</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+52 55 1234 5678"
                  />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="address">Dirección</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Av. Principal 123, Ciudad"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="category">Categoría</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    {SUPPLIER_CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>
                        {cat}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="notes">Notas</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Notas adicionales..."
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="btn-industrial" data-testid="save-supplier-btn">
                Registrar Proveedor
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Suppliers;
